import { useMutation, useQuery } from '@tanstack/react-query';
import { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import { customFetcher } from '../useFetch';
import { ILegalRequirementTemplateFullObject } from '../../Interfaces/ILegalRequirementTemplate';
import { ILegalRequirement } from '../../Interfaces/ILegalRequirement';

//////////////////// LegalRequirementStates
export const useGetLegalRequirementStates = () => {
  const {
    data: legalRequirementStates,
    isLoading: isLoadingLegalRequirementStates,
    refetch: refetchLegalRequirementStates,
  } = useQuery<ILegalRequirementState[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalrequirementstate'],
    queryFn: () => customFetcher({ endpoint: '/legalrequirementstate' }),
  });

  return {
    legalRequirementStates,
    isLoadingLegalRequirementStates,
    refetchLegalRequirementStates,
  };
};

//////////////////// LegalRequirement
export const useGetLegalRequirements = () => {
  const { data: legalRequirements, isLoading: isLoadingLegalRequirements } =
    useQuery<ILegalRequirement[]>({
      queryKey: ['legalrequirement'],
      queryFn: () => customFetcher({ endpoint: '/legalrequirement' }),
    });

  return { legalRequirements, isLoadingLegalRequirements };
};

export const useGetLegalRequirementsSmall = (searchUri?: string) => {
  const { data: legReqs, isLoading: isLoadingLegReqs } = useQuery<
    ILegalRequirement[]
  >({
    queryKey: ['legalrequirement/small', 'search', searchUri],
    queryFn: () =>
      customFetcher({ endpoint: `/legalrequirement/small?${searchUri}` }),
  });

  return { legReqs, isLoadingLegReqs };
};

export const useGetLegalRequirementsExpired = (searchUri?: string) => {
  const { data: legReqs, isLoading: isLoadingLegReqs } = useQuery<
    ILegalRequirement[]
  >({
    queryKey: ['legalrequirement/expired', 'search', searchUri],
    queryFn: () =>
      customFetcher({ endpoint: `/legalrequirement/expired?${searchUri}` }),
  });

  return { legReqs, isLoadingLegReqs };
};

//////////////////// LegalRequirementTemplate
export const useGetLegalRequirementTemplates = () => {
  const {
    data: legalRequirementTemplates,
    isLoading: isLoadingLegalRequirementTemplates,
    refetch: refetchLegalRequirementTemplates,
    isRefetching: isRefetchingLegalRequirementTemplates,
  } = useQuery<ILegalRequirementTemplateFullObject[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalrequirementtemplate'],
    queryFn: () => customFetcher({ endpoint: '/legalrequirementtemplate' }),
  });
  return {
    legalRequirementTemplates,
    isLoadingLegalRequirementTemplates,
    refetchLegalRequirementTemplates,
    isRefetchingLegalRequirementTemplates,
  };
};
export const useGetLegalRequirementTemplateFullObjects = () => {
  const {
    data: legalRequirementTemplateFullObjects,
    isLoading: isLoadinglegalRequirementTemplateFullObjects,
    refetch: refetchlegalRequirementTemplateFullObjects,
    isRefetching: isRefetchinglegalRequirementTemplateFullObjects,
  } = useQuery<ILegalRequirementTemplateFullObject[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalrequirementtemplate/getfullobjects'],
    queryFn: () =>
      customFetcher({ endpoint: '/legalrequirementtemplate/getfullobjects' }),
  });
  return {
    legalRequirementTemplateFullObjects,
    isLoadinglegalRequirementTemplateFullObjects,
    refetchlegalRequirementTemplateFullObjects,
    isRefetchinglegalRequirementTemplateFullObjects,
  };
};

export const useGetLegalRequirementTemplateById = (
  idLegalRequirement: number
) => {
  const {
    data: legalRequirementTemplate,
    isLoading: isLoadingLegalRequirementTemplate,
    isFetching: isLegalRequirementTemplateFetching,
    //isRefetching
    refetch: refetchLegalRequirementTemplate,
  } = useQuery<ILegalRequirementTemplateFullObject>({
    refetchOnWindowFocus: false,
    queryKey: ['/legalrequirementtemplate/fullObject/', idLegalRequirement],
    queryFn: () =>
      customFetcher({
        endpoint: `/legalrequirementtemplate/fullObject/${idLegalRequirement}`,
      }),
  });
  return {
    legalRequirementTemplate,
    isLoadingLegalRequirementTemplate,
    refetchLegalRequirementTemplate,
    isLegalRequirementTemplateFetching,
  };
};

export const useGetLegalRequirementTemplateFullObjectsByIds = (
  idsLegalRequirement: number[]
) => {
  const idsParam = idsLegalRequirement.join(','); // IDs in CSV-Format umwandeln
  const {
    data: LegalRequirementTemplateFullObjectsByIds,
    isLoading: isLoadingLegalRequirementTemplateFullObjectsByIds,
    refetch: refetchLegalRequirementTemplateFullObjectsByIds,
  } = useQuery<ILegalRequirementTemplateFullObject[]>({
    refetchOnWindowFocus: false,
    queryKey: [
      '/legalrequirementtemplate/getlegalrequirementtemplatefullobjectsbyids',
      idsLegalRequirement,
    ],
    queryFn: () =>
      customFetcher({
        endpoint: `/legalrequirementtemplate/getlegalrequirementtemplatefullobjectsbyids?ids=${idsParam}`,
        method: 'GET',
      }),
    enabled: idsLegalRequirement.length > 0,
  });
  return {
    LegalRequirementTemplateFullObjectsByIds,
    isLoadingLegalRequirementTemplateFullObjectsByIds,
    refetchLegalRequirementTemplateFullObjectsByIds,
  };
};

export const useUpdateLegalRequirementTemplates = () => {
  const {
    mutateAsync: updateLegalRequirementTemplates,
    isPending: isPendingUpdateLegalRequirementTemplates,
  } = useMutation({
    mutationFn: (uploadObject: ILegalRequirementTemplateFullObject) =>
      customFetcher({
        endpoint: '/legalrequirementtemplate/updatefullobject',
        body: uploadObject,
        method: 'PUT',
      }),
  });
  return {
    updateLegalRequirementTemplates,
    isPendingUpdateLegalRequirementTemplates,
  };
};
